import { getLocalizedName } from "@/utils";
import { GetCustomer } from "@/server-types";
import { Card, CardBody, Button, useDisclosure } from "@heroui/react";
import {
  Calendar,
  MapPin,
  DoorOpen,
  User,
  FileText,
  AlertCircle,
  History,
  LucideIcon,
} from "lucide-react";
import dayjs from "dayjs";
import { t } from "@lingui/core/macro";
import {
  getUserAppointmentCancelReasonOptions,
  getAppointmentCancelReasonOptions,
} from "@/constants/cancelReasons";

import CustomerAppointmentLogModal from "./CustomerAppointmentLogModal";
import { CustomerAppointmentActions } from "./CustomerAppointmentActions";
import CustomerAppointmentComment from "./CustomerAppointmentComment";

type InfoRowProps = {
  icon: LucideIcon;
  text: string;
  className?: string;
};

type AlertMessageProps = {
  icon?: LucideIcon;
  message: string;
  type?: "danger" | "yellow";
};

const InfoRow = ({ icon: Icon, text, className = "" }: InfoRowProps) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <Icon className={`h-4 w-4 ${className ? "" : "text-gray-500"}`} />
    <span>{text}</span>
  </div>
);

const AlertMessage = ({
  icon: Icon = AlertCircle,
  message,
  type = "danger",
}: AlertMessageProps) => (
  <div className={`flex items-center gap-2 text-${type}-600`}>
    <Icon className="h-4 w-4" />
    <span>{message}</span>
  </div>
);

export default function CustomerAppointmentListItem({
  customerAppointment,
}: {
  customerAppointment: GetCustomer["response"]["customerAppointments"][0];
}) {
  const dateTime = dayjs(customerAppointment.appointment.startTimeLocal);

  const customerAppointmentCancelReasons =
    getUserAppointmentCancelReasonOptions();

  const appointmentCancelReasons = getAppointmentCancelReasonOptions();

  const { isOpen: isLogModalOpen, onOpenChange: onLogModalOpenChange } =
    useDisclosure();

  return (
    <>
      <Card className="border-1 border-gray-200 bg-default-50 shadow-sm">
        <CardBody className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                <span className="font-medium">
                  {getLocalizedName(
                    customerAppointment.appointment.service.nameLocalized,
                  )}
                </span>
                {customerAppointment.price && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">
                      {customerAppointment.price}
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span>{dateTime.format("DD.MM.YYYY")}</span>
                  <span className="mx-1 text-gray-500">•</span>
                  <span>{dateTime.format("HH:mm")}</span>
                </div>

                <InfoRow
                  icon={MapPin}
                  text={customerAppointment.appointment.location.label}
                />

                {customerAppointment.appointment.appointmentResources.length >
                  0 && (
                  <InfoRow
                    icon={DoorOpen}
                    text={customerAppointment.appointment.appointmentResources
                      .map((r) => r.resource.label)
                      .join(", ")}
                  />
                )}

                {customerAppointment.appointment.appointmentUsers.length >
                  0 && (
                  <InfoRow
                    icon={User}
                    text={customerAppointment.appointment.appointmentUsers
                      .map((u) => `${u.user.name} ${u.user.lastName}`)
                      .join(", ")}
                  />
                )}

                {customerAppointment.comment && (
                  <InfoRow
                    icon={FileText}
                    text={
                      customerAppointment.comment.length > 100
                        ? `${customerAppointment.comment.substring(0, 100)}...`
                        : customerAppointment.comment
                    }
                  />
                )}

                {customerAppointment.appointment.cancelReason &&
                  customerAppointment.appointment.isDeleted && (
                    <AlertMessage
                      message={`${t`Razlog za odpoved`}: ${
                        customerAppointmentCancelReasons.find(
                          (r) => r.value === customerAppointment.cancelReason,
                        )?.label
                      }`}
                    />
                  )}

                {customerAppointment.cancelReason &&
                  customerAppointment.isDeleted && (
                    <AlertMessage
                      message={`${t`Razlog za odstranitev stranke s termina`}: ${
                        appointmentCancelReasons.find(
                          (r) => r.value === customerAppointment.cancelReason,
                        )?.label
                      }`}
                    />
                  )}

                {customerAppointment.customerDidAttend === -1 && (
                  <AlertMessage message={t`Stranka ni prišla na termin`} />
                )}

                {customerAppointment.appointment.isPreScheduled && (
                  <AlertMessage message={t`Razpisan termin`} type="yellow" />
                )}
              </div>
            </div>

            <div className="flex gap-1">
              <CustomerAppointmentComment
                customerAppointmentId={customerAppointment.userAppointmentId}
                initialComment={customerAppointment.comment || undefined}
              />
              <CustomerAppointmentActions
                appointment={{
                  appointmentId: customerAppointment.appointmentId,
                  isRepeating:
                    customerAppointment.appointment.repeatingAppointmentId !==
                    null,
                  isLinked:
                    customerAppointment.appointment.linkedAppointmentId !==
                    null,
                  customerAppointment: {
                    userAppointmentId: customerAppointment.userAppointmentId,
                    customerDidAttend: customerAppointment.customerDidAttend,
                  },
                  isPreScheduled:
                    customerAppointment.appointment.isPreScheduled,
                }}
              />
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={onLogModalOpenChange}
              >
                <History className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
      <CustomerAppointmentLogModal
        isOpen={isLogModalOpen}
        onOpenChange={onLogModalOpenChange}
        appointmentId={customerAppointment.appointmentId}
      />
    </>
  );
}
